import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Library,
  Settings,
  Home,
  LayoutPanelTop,
  BookOpen,
  Tags,
  Languages,
  FileText,
  ImageIcon,
  Network,
  Blocks,
  MessageSquare,
  Workflow,
  Variable,
} from "lucide-react";
import { UnsavedChangesDialog } from   '@/components/ui/UnsavedChangesDialog';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

// Maximum number of history entries per tab
const MAX_HISTORY_ENTRIES = 20;

// Function to get icon for a tab based on its path
const getTabIcon = (path: string): React.ComponentType<{ className?: string }> => {
  // Remove any trailing slashes and parameters for matching
  const cleanPath = path.split('?')[0].replace(/\/$/, '');
  
  // Map paths to their corresponding sidebar icons
  if (cleanPath.startsWith('/library')) return Library;
  if (cleanPath.startsWith('/templates')) return LayoutPanelTop;
  if (cleanPath.startsWith('/types-and-tags')) return Tags;
  if (cleanPath.startsWith('/tms')) return Languages;
  if (cleanPath.startsWith('/documents')) return BookOpen;
  if (cleanPath.startsWith('/dialogs')) return MessageSquare;
  if (cleanPath.startsWith('/logic')) return Workflow;
  if (cleanPath.startsWith('/settings')) return Settings;
  if (cleanPath.startsWith('/home')) return Home;
  if (cleanPath.startsWith('/images')) return ImageIcon;
  if (cleanPath.startsWith('/ui')) return Blocks;
  if (cleanPath.startsWith('/relationship')) return Network;
  if (cleanPath.startsWith('/variables')) return Variable;
  
  // Default fallback icon
  return FileText;
};

export type TabPanel = 'left' | 'right';

export interface TabHistoryEntry {
  path: string;
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface Tab {
  id: string;
  title: string;
  path: string;
  isActive: boolean;
  panel: TabPanel; // Which panel this tab belongs to
  icon?: React.ComponentType<{ className?: string }>;
  baseTitle?: string; // Store the original title without folder info
  folderInfo?: {
    folderId: string | null;
    folderName: string | null;
    itemType: string;
  };
  hasUnsavedChanges?: boolean; // Track if tab has unsaved changes
  onSaveChanges?: () => Promise<void> | void; // Callback to save changes
  onDiscardChanges?: () => void; // Callback to clean up when discarding changes (e.g., clear drafts)
  onBeforeClose?: () => Promise<void> | void; // Callback to clean up before tab closes (e.g., release locks)
  // History tracking for in-tab navigation
  history: TabHistoryEntry[]; // Stack of paths (max 20)
  historyIndex: number; // Current position in history (-1 means at current, 0+ means in history)
}

export interface SplitView {
  id: string;
  tabId: string; // Single tab that's split
  isActive: boolean;
}

interface TabContextType {
  tabs: Tab[];
  activeTabId: string | null;
  activePanel: TabPanel; // Which panel is currently focused
  splitViews: SplitView[];
  openTab: (tab: Omit<Tab, 'isActive' | 'panel' | 'history' | 'historyIndex'>, options?: { activate?: boolean }) => void;
  closeTab: (tabId: string, force?: boolean) => void;
  switchToTab: (tabId: string) => void;
  switchToRightTab: (tabId: string) => void; // Switch to a tab in the right panel
  focusPanel: (panel: TabPanel) => void; // Focus a panel and update URL to its active tab
  updateTabTitle: (tabId: string, title: string) => void;
  updateTabFolderInfo: (tabId: string, folderId: string | null, folderName: string | null, itemType: string) => void;
  updateTabUnsavedChanges: (tabId: string, hasChanges: boolean, onSave?: () => Promise<void> | void, onDiscard?: () => void) => void;
  updateTabCleanup: (tabId: string, onBeforeClose?: () => Promise<void>) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  createSplitView: (tabId: string) => void;
  closeSplitView: (splitViewId: string) => void;
  switchToSplitView: (splitViewId: string) => void;
  requestTabClose: (tabId: string) => Promise<boolean>; // Returns true if can close, false if cancelled
  // Helper functions to get tabs by panel
  getLeftTabs: () => Tab[];
  getRightTabs: () => Tab[];
  hasSplitPanel: boolean; // Whether the right panel is active
  // In-tab navigation methods
  navigateInTab: (path: string, title: string, tabId?: string) => void; // Navigate within a tab (updates history)
  goBack: (tabId?: string) => void; // Go back in tab history
  goForward: (tabId?: string) => void; // Go forward in tab history
  canGoBack: (tabId?: string) => boolean; // Check if can go back
  canGoForward: (tabId?: string) => boolean; // Check if can go forward
  isHistoryNavigation: boolean; // Flag to indicate we're navigating via history (back/forward)
}

const TabContext = createContext<TabContextType | null>(null);

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return context;
};

interface TabProviderProps {
  children: ReactNode;
}

export const TabProvider: React.FC<TabProviderProps> = ({ children }) => {
  const { t } = usePlatformTranslation();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<TabPanel>('left');
  const [splitViews, setSplitViews] = useState<SplitView[]>([]);
  const [pendingCloseTabId, setPendingCloseTabId] = useState<string | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Ref to always have the latest tabs (avoids stale closure issues)
  const tabsRef = useRef<Tab[]>(tabs);
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);
  
  // Ref to track when we're navigating via history (back/forward)
  // This prevents useAutoTabs from re-processing the navigation
  const isHistoryNavigationRef = useRef<boolean>(false);
  const [isHistoryNavigation, setIsHistoryNavigation] = useState(false);

  // Handle browser tab close: warn if unsaved changes and release all locks
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Check if ANY tab has unsaved changes
      const hasAnyUnsavedChanges = tabsRef.current.some(tab => tab.hasUnsavedChanges);
      
      // Release all locks via each tab's onBeforeClose callback
      // This ensures locks are cleaned up even if user confirms to leave
      tabsRef.current.forEach(tab => {
        if (tab.onBeforeClose) {
          // Call lock release for each tab
          // Note: beforeunload has limited time, but fetch with keepalive should work
          Promise.resolve(tab.onBeforeClose()).catch(err => {
            console.error('[TabContext] Error releasing lock on beforeunload:', err);
          });
        }
      });
      
      // Show browser warning if unsaved changes exist
      if (hasAnyUnsavedChanges) {
        // Prevent default and show browser warning
        event.preventDefault();
        // Modern browsers ignore custom messages for security
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Uses tabsRef.current which is always up-to-date

  const openTab = useCallback((newTab: Omit<Tab, 'isActive' | 'panel' | 'history' | 'historyIndex'>, options?: { activate?: boolean }) => {
    let newActiveTabId: string | null = null;

    setTabs(prevTabs => {
      // Default to activate unless explicitly set to false
      const shouldActivate = options?.activate !== false;
      
      // Check if tab already exists by ID
      const existingTabById = prevTabs.find(tab => tab.id === newTab.id);
      if (existingTabById) {
        // Tab exists, activate it only if shouldActivate is true
        if (shouldActivate) {
          newActiveTabId = existingTabById.id;
          return prevTabs.map(tab => ({
            ...tab,
            isActive: tab.id === existingTabById.id
          }));
        }
        return prevTabs; // No change if not activating
      }
      
      // Also check if a tab with the same PATH exists (for in-tab navigation scenarios)
      const existingTabByPath = prevTabs.find(tab => tab.path === newTab.path);
      if (existingTabByPath) {
        // Tab with this path exists, activate it
        if (shouldActivate) {
          newActiveTabId = existingTabByPath.id;
          return prevTabs.map(tab => ({
            ...tab,
            isActive: tab.id === existingTabByPath.id
          }));
        }
        return prevTabs; // No change if not activating
      }

      // Create new tab with icon, base title, and initialized history
      const initialHistoryEntry: TabHistoryEntry = {
        path: newTab.path,
        title: newTab.title,
        icon: newTab.icon || getTabIcon(newTab.path),
      };

      if (shouldActivate) {
        const newTabs = prevTabs.map(tab => ({ ...tab, isActive: false }));
        const tabToAdd: Tab = { 
          ...newTab, 
          isActive: true,
          panel: 'left',
          icon: newTab.icon || getTabIcon(newTab.path),
          baseTitle: newTab.baseTitle || newTab.title,
          folderInfo: newTab.folderInfo || undefined,
          history: [initialHistoryEntry],
          historyIndex: 0,
        };
        newActiveTabId = newTab.id;
        return [...newTabs, tabToAdd];
      } else {
        // Add new tab but don't activate it
        const tabToAdd: Tab = { 
          ...newTab, 
          isActive: false,
          panel: 'left',
          icon: newTab.icon || getTabIcon(newTab.path),
          baseTitle: newTab.baseTitle || newTab.title,
          folderInfo: newTab.folderInfo || undefined,
          history: [initialHistoryEntry],
          historyIndex: 0,
        };
        return [...prevTabs, tabToAdd];
      }
    });

    // Perform activeTabId update after state update
    if (newActiveTabId !== null) {
      setActiveTabId(newActiveTabId);
    }
  }, []);

  const closeTab = useCallback((tabId: string, force: boolean = false) => {
    // First, call cleanup handler before closing (fire-and-forget, don't await)
    const tab = tabs.find(t => t.id === tabId);
    console.log('[TabContext] closing tab:', tabId, tab?.onBeforeClose);
    if (tab?.onBeforeClose) {
      // Call the cleanup handler (may be async, but we don't wait)
      Promise.resolve(tab.onBeforeClose()).then(() => {
        console.log('[TabContext] onBeforeClose completed for tab:', tabId);
      }).catch((error) => {
        console.error('[TabContext] Error in onBeforeClose handler:', error);
      });
    }

    // PRE-COMPUTE navigation info BEFORE calling setTabs
    // This ensures we have the navigation target determined before any state updates
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    
    // Tab not found
    if (tabIndex === -1) {
      return;
    }
    
    // If not forcing and tab has unsaved changes, don't close directly
    if (!force) {
      const tabToClose = tabs[tabIndex];
      if (tabToClose?.hasUnsavedChanges) {
        // Don't close, let requestTabClose handle it
        return;
      }
    }

    const filteredTabs = tabs.filter(t => t.id !== tabId);
    const closingTab = tabs[tabIndex];
    const isClosingActiveTab = closingTab?.isActive === true;
    
    let newActiveTabId: string | null = null;
    let navigationPath: string | null = null;
    let shouldClearActiveTab = false;
    
    // Determine navigation target BEFORE state update
    if (isClosingActiveTab && filteredTabs.length > 0) {
      // Switch to the tab on the left of the closed tab, or the first tab if at index 0
      const leftTabIndex = Math.max(0, tabIndex - 1);
      const newActiveIndex = tabIndex > 0 ? leftTabIndex : 0;
      const newActiveTab = filteredTabs[newActiveIndex] || filteredTabs[0];
      
      newActiveTabId = newActiveTab.id;
      navigationPath = newActiveTab.path;
    } else if (filteredTabs.length === 0) {
      // Closing the last tab - navigate to home
      shouldClearActiveTab = true;
      navigationPath = '/home';
    }

    // Now update state with the tab removed
    setTabs(prevTabs => {
      const tabIndex = prevTabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return prevTabs;
      
      const filteredTabs = prevTabs.filter(tab => tab.id !== tabId);
      const closingTab = prevTabs[tabIndex];
      const isClosingActiveTab = closingTab?.isActive === true;
      
      // If we're closing the active tab, mark the new active tab
      if (isClosingActiveTab && filteredTabs.length > 0 && newActiveTabId) {
        // Also update the ref synchronously
        tabsRef.current = filteredTabs.map(tab => ({
          ...tab,
          isActive: tab.id === newActiveTabId
        }));
        return tabsRef.current;
      }
      
      // Also update ref
      tabsRef.current = filteredTabs;
      return filteredTabs;
    });
    
    // Perform navigation immediately after state update
    if (newActiveTabId !== null) {
      setActiveTabId(newActiveTabId);
    }
    if (shouldClearActiveTab) {
      setActiveTabId(null);
      // When closing the last tab, open a new home tab
      setTimeout(() => {
        openTab({
          id: '/home',
          title: 'Home',
          path: '/home',
        }, { activate: true });
      }, 0);
    }
    if (navigationPath !== null) {
      navigate(navigationPath);
    }
  }, [tabs, navigate, openTab]);

  const switchToTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      // Update tab state
      setActiveTabId(tabId);
      setActivePanel('left');
      setTabs(prevTabs => prevTabs.map(t => ({
        ...t,
        isActive: t.id === tabId
      })));
      
      // Navigate to the tab's path
      navigate(tab.path);
      
      // Emit event to update folder selection in sidebar when switching tabs
      // Always emit the event, even if no folder info (for "All Items" state)
      window.dispatchEvent(new CustomEvent('tabSwitched', {
        detail: {
          tabId,
          folderInfo: tab.folderInfo || null
        }
      }));
    }
  }, [tabs, navigate, activeTabId]);

  // Switch to a tab in the right panel and update URL to its path
  const switchToRightTab = useCallback((tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab && tab.panel === 'right') {
      setActivePanel('right'); // Focus on right panel
      navigate(tab.path); // Update URL to the right panel's tab path
    }
  }, [tabs, navigate]);

  // Focus a panel and update URL to its active tab's path
  const focusPanel = useCallback((panel: TabPanel) => {
    setActivePanel(panel);
    if (panel === 'left') {
      // Find the active left tab and navigate to it
      const activeLeftTab = tabs.find(t => (t.panel === 'left' || !t.panel) && t.isActive);
      if (activeLeftTab) {
        navigate(activeLeftTab.path);
      }
    } else {
      // Find the right panel tab and navigate to it
      const rightTab = tabs.find(t => t.panel === 'right');
      if (rightTab) {
        navigate(rightTab.path);
      }
    }
  }, [tabs, navigate]);

  const updateTabTitle = useCallback((tabId: string, title: string) => {
    setTabs(prevTabs => prevTabs.map(tab => 
      tab.id === tabId ? { ...tab, title } : tab
    ));
  }, []);

  const updateTabFolderInfo = useCallback((tabId: string, folderId: string | null, folderName: string | null, itemType: string) => {
    setTabs(prevTabs => prevTabs.map(tab => {
      if (tab.id === tabId) {
        const baseTitle = tab.baseTitle || tab.title;
        let newTitle = baseTitle;
        
        // Update title based on folder selection
        if (folderId && folderName) {
          newTitle = `${baseTitle} (.../${folderName})`;
        }
        
        return {
          ...tab,
          title: newTitle,
          baseTitle,
          folderInfo: {
            folderId,
            folderName,
            itemType
          }
        };
      }
      return tab;
    }));
  }, []);

  // Navigate within a tab (updates history stack)
  // Note: This function only updates the tab state. The actual navigation is handled
  // by the caller (useAutoTabs) which reacts to URL changes.
  const navigateInTab = useCallback((path: string, title: string, tabId?: string) => {
    const targetTabId = tabId || activeTabId;
    if (!targetTabId) return;

    let shouldUpdateActiveTab = false;

    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        // Only update the target tab, leave others unchanged
        if (tab.id !== targetTabId) {
          // If this is not the target tab, ensure isActive reflects the correct state
          // Only the target tab should be active
          if (tab.isActive) {
            return { ...tab, isActive: false };
          }
          return tab;
        }
        
        // This is the target tab - update it
        
        // Don't add to history if we're already at this path
        if (tab.path === path) {
          // Still need to ensure this tab is active
          if (!tab.isActive) {
            shouldUpdateActiveTab = true;
            return { ...tab, isActive: true };
          }
          return tab;
        }
        
        // Create new history entry
        const newHistoryEntry: TabHistoryEntry = {
          path,
          title,
          icon: getTabIcon(path),
        };
        
        // Ensure history exists (defensive check)
        const currentHistory = tab.history || [];
        const currentIndex = tab.historyIndex ?? currentHistory.length - 1;
        
        // If we're not at the end of history (user navigated back), 
        // truncate history from current position
        let newHistory = currentHistory.slice(0, currentIndex + 1);
        
        // Add new entry
        newHistory.push(newHistoryEntry);
        
        // Limit history to MAX_HISTORY_ENTRIES
        if (newHistory.length > MAX_HISTORY_ENTRIES) {
          newHistory = newHistory.slice(newHistory.length - MAX_HISTORY_ENTRIES);
        }
        
        shouldUpdateActiveTab = true;
        
        return {
          ...tab,
          path,
          title,
          icon: getTabIcon(path),
          history: newHistory,
          historyIndex: newHistory.length - 1,
          isActive: true, // Ensure this tab is active
        };
      });
    });
    
    // Update activeTabId if needed
    if (shouldUpdateActiveTab || activeTabId !== targetTabId) {
      setActiveTabId(targetTabId);
    }
    
    // Don't call navigate() here - the navigation already happened via the original
    // navigate() call that triggered useAutoTabs, which then called this function.
  }, [activeTabId]);

  // Go back in tab history
  const goBack = useCallback((tabId?: string) => {
    const targetTabId = tabId || activeTabId;
    if (!targetTabId) return;

    let navigationPath: string | null = null;

    // Set flag to indicate we're navigating via history
    // This prevents useAutoTabs from re-processing this navigation
    isHistoryNavigationRef.current = true;
    setIsHistoryNavigation(true);

    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id !== targetTabId) return tab;
        
        // Defensive checks for history
        if (!tab.history || tab.historyIndex === undefined) return tab;
        
        // Can't go back if at the beginning of history
        if (tab.historyIndex <= 0) return tab;
        
        const newIndex = tab.historyIndex - 1;
        const historyEntry = tab.history[newIndex];
        
        if (!historyEntry) return tab;
        
        navigationPath = historyEntry.path;
        
        return {
          ...tab,
          path: historyEntry.path,
          title: historyEntry.title,
          icon: historyEntry.icon || getTabIcon(historyEntry.path),
          historyIndex: newIndex,
        };
      });
    });

    if (navigationPath) {
      navigate(navigationPath);
      // Clear flag after a short delay to allow useAutoTabs to check it
      setTimeout(() => {
        isHistoryNavigationRef.current = false;
        setIsHistoryNavigation(false);
      }, 50);
    } else {
      // Clear flag if we didn't navigate
      isHistoryNavigationRef.current = false;
      setIsHistoryNavigation(false);
    }
  }, [activeTabId, navigate]);

  // Go forward in tab history
  const goForward = useCallback((tabId?: string) => {
    const targetTabId = tabId || activeTabId;
    if (!targetTabId) return;

    let navigationPath: string | null = null;

    // Set flag to indicate we're navigating via history
    // This prevents useAutoTabs from re-processing this navigation
    isHistoryNavigationRef.current = true;
    setIsHistoryNavigation(true);

    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id !== targetTabId) return tab;
        
        // Defensive checks for history
        if (!tab.history || tab.historyIndex === undefined) return tab;
        
        // Can't go forward if at the end of history
        if (tab.historyIndex >= tab.history.length - 1) return tab;
        
        const newIndex = tab.historyIndex + 1;
        const historyEntry = tab.history[newIndex];
        
        if (!historyEntry) return tab;
        
        navigationPath = historyEntry.path;
        
        return {
          ...tab,
          path: historyEntry.path,
          title: historyEntry.title,
          icon: historyEntry.icon || getTabIcon(historyEntry.path),
          historyIndex: newIndex,
        };
      });
    });

    if (navigationPath) {
      navigate(navigationPath);
      // Clear flag after a short delay to allow useAutoTabs to check it
      setTimeout(() => {
        isHistoryNavigationRef.current = false;
        setIsHistoryNavigation(false);
      }, 50);
    } else {
      // Clear flag if we didn't navigate
      isHistoryNavigationRef.current = false;
      setIsHistoryNavigation(false);
    }
  }, [activeTabId, navigate]);

  // Check if can go back in tab history
  const canGoBack = useCallback((tabId?: string): boolean => {
    const targetTabId = tabId || activeTabId;
    if (!targetTabId) return false;
    
    const tab = tabs.find(t => t.id === targetTabId);
    if (!tab || !tab.history || tab.historyIndex === undefined) return false;
    
    return tab.historyIndex > 0;
  }, [activeTabId, tabs]);

  // Check if can go forward in tab history
  const canGoForward = useCallback((tabId?: string): boolean => {
    const targetTabId = tabId || activeTabId;
    if (!targetTabId) return false;
    
    const tab = tabs.find(t => t.id === targetTabId);
    if (!tab || !tab.history || tab.historyIndex === undefined) return false;
    
    return tab.historyIndex < tab.history.length - 1;
  }, [activeTabId, tabs]);

  const updateTabUnsavedChanges = useCallback((tabId: string, hasChanges: boolean, onSave?: () => Promise<void> | void, onDiscard?: () => void) => {
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, hasUnsavedChanges: hasChanges, onSaveChanges: onSave, onDiscardChanges: onDiscard } : tab
      );
      // Also update the ref synchronously so requestTabClose can see the changes immediately
      tabsRef.current = newTabs;
      return newTabs;
    });
  }, []);

  const updateTabCleanup = useCallback((tabId: string, onBeforeClose?: () => Promise<void>) => {
    setTabs(prevTabs => {
      const newTabs = prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, onBeforeClose } : tab
      );
      // Also update the ref synchronously
      tabsRef.current = newTabs;
      return newTabs;
    });
  }, []);

  /**
   * Request to close a tab - will show dialog if there are unsaved changes
   * Returns a promise that resolves to true if the tab can be closed
   */
  const requestTabClose = useCallback(async (tabId: string): Promise<boolean> => {
    // Use ref to get the latest tabs (avoids stale closure issues when called
    // immediately after updateTabUnsavedChanges)
    const currentTabs = tabsRef.current;
    const tab = currentTabs.find(t => t.id === tabId);
    
    if (!tab) {
      return true; // Tab doesn't exist, allow close
    }

    if (!tab.hasUnsavedChanges) {
      closeTab(tabId, true);
      return true;
    }

    // Show unsaved changes dialog
    return new Promise((resolve) => {
      setPendingCloseTabId(tabId);
      setShowUnsavedDialog(true);
      
      // Store the resolve function so we can call it from dialog handlers
      (window as any).__tabCloseResolve = resolve;
    });
  }, [closeTab]);

  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    setTabs(prevTabs => {
      const newTabs = [...prevTabs];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);
      return newTabs;
    });
  }, []);

  const createSplitView = useCallback((tabId: string) => {
    // First, find the tab and determine what to activate BEFORE updating state
    const currentTabs = tabs;
    const tabToSplit = currentTabs.find(t => t.id === tabId);
    
    if (!tabToSplit) {
      console.warn('[createSplitView] Tab not found:', tabId);
      return;
    }
    
    // Find a left panel tab to activate (prefer the first one, or another left tab)
    const leftTabs = currentTabs.filter(t => (!t.panel || t.panel === 'left') && t.id !== tabId);
    const tabToActivate = leftTabs[0];

    // Move the tab from left panel to right panel and activate another left tab
    setTabs(prevTabs => {
      return prevTabs.map(t => {
        if (t.id === tabId) {
          // Move to right panel
          return { ...t, panel: 'right' as TabPanel, isActive: false };
        }
        if (tabToActivate && t.id === tabToActivate.id) {
          // Activate this tab in the left panel
          return { ...t, isActive: true };
        }
        // Deactivate other tabs
        return { ...t, isActive: false };
      });
    });

    // Navigate to the newly activated left tab SYNCHRONOUSLY
    // (navigation happens immediately, state update is batched)
    if (tabToActivate) {
      setActiveTabId(tabToActivate.id);
      navigate(tabToActivate.path);
    }

    const newSplitView: SplitView = {
      id: `split-${Date.now()}`,
      tabId: tabId,
      isActive: true
    };

    setSplitViews(prev => [...prev, newSplitView]);
  }, [tabs, navigate]);

  const closeSplitView = useCallback((splitViewId: string) => {
    // Find the split view to get the tab ID
    const splitView = splitViews.find(sv => sv.id === splitViewId);
    if (splitView) {
      // Close the tab entirely (per user preference)
      closeTab(splitView.tabId, true);
    }
    
    // Remove the split view
    setSplitViews(prev => prev.filter(sv => sv.id !== splitViewId));
  }, [splitViews, closeTab]);

  const switchToSplitView = useCallback((splitViewId: string) => {
    setSplitViews(prev => prev.map(sv => ({
      ...sv,
      isActive: sv.id === splitViewId
    })));
  }, []);

  // Handle unsaved changes dialog actions
  const handleSaveAndClose = useCallback(async () => {
    if (!pendingCloseTabId) return;
    
    const tab = tabs.find(t => t.id === pendingCloseTabId);
    if (tab?.onSaveChanges) {
      try {
        await tab.onSaveChanges();
        closeTab(pendingCloseTabId, true);
        setShowUnsavedDialog(false);
        setPendingCloseTabId(null);
        
        // Resolve the promise
        if ((window as any).__tabCloseResolve) {
          (window as any).__tabCloseResolve(true);
          delete (window as any).__tabCloseResolve;
        }
      } catch (error) {
        console.error('Error saving changes:', error);
        // Keep dialog open on error
      }
    }
  }, [pendingCloseTabId, tabs, closeTab]);

  const handleDiscardAndClose = useCallback(() => {
    if (!pendingCloseTabId) return;
    
    // Call discard callback to clean up (e.g., clear drafts)
    const tab = tabs.find(t => t.id === pendingCloseTabId);
    if (tab?.onDiscardChanges) {
      tab.onDiscardChanges();
    }
    
    closeTab(pendingCloseTabId, true);
    setShowUnsavedDialog(false);
    setPendingCloseTabId(null);
    
    // Resolve the promise
    if ((window as any).__tabCloseResolve) {
      (window as any).__tabCloseResolve(true);
      delete (window as any).__tabCloseResolve;
    }
  }, [pendingCloseTabId, closeTab, tabs]);

  const handleCancelClose = useCallback(() => {
    setShowUnsavedDialog(false);
    setPendingCloseTabId(null);
    
    // Resolve the promise with false (cancelled)
    if ((window as any).__tabCloseResolve) {
      (window as any).__tabCloseResolve(false);
      delete (window as any).__tabCloseResolve;
    }
  }, []);

  // Helper functions to get tabs by panel
  // Default to 'left' if panel is not set (for backwards compatibility)
  const getLeftTabs = useCallback(() => {
    return tabs.filter(t => !t.panel || t.panel === 'left');
  }, [tabs]);

  const getRightTabs = useCallback(() => {
    return tabs.filter(t => t.panel === 'right');
  }, [tabs]);

  // Whether the right panel is active (has split views)
  const hasSplitPanel = splitViews.length > 0;

  // Keyboard shortcuts for back/forward navigation (Alt+Left/Right)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt+Left for going back
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      }
      // Alt+Right for going forward
      if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        goForward();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goBack, goForward]);

  return (
    <TabContext.Provider value={{
      tabs,
      activeTabId,
      activePanel,
      splitViews,
      openTab,
      closeTab,
      switchToTab,
      switchToRightTab,
      focusPanel,
      updateTabTitle,
      updateTabFolderInfo,
      updateTabUnsavedChanges,
      updateTabCleanup,
      reorderTabs,
      createSplitView,
      closeSplitView,
      switchToSplitView,
      requestTabClose,
      getLeftTabs,
      getRightTabs,
      hasSplitPanel,
      navigateInTab,
      goBack,
      goForward,
      canGoBack,
      canGoForward,
      isHistoryNavigation,
    }}>
      {children}
      
      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={handleCancelClose}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
        title={t('platform.general.tabcontext.unsaved_changes')}
        description="You have unsaved changes in this tab. Would you like to save them before closing?"
      />
    </TabContext.Provider>
  );
};
